import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// hooks
import useNotifications, { AppNotification } from '../../../hooks/useNotifications';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';

// ----------------------------------------------------------------------
// Real, role-filtered notifications fed by useNotifications (UI/UX plan §6).
// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const { items, unreadCount, markRead, markAllAsRead, isUnread, refresh } = useNotifications();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
    refresh();
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleItemClick = (n: AppNotification) => {
    markRead(n); // mark only this one read (badge -1), not all
    handleClose();
    navigate(n.href);
  };

  const unread = items.filter((n) => isUnread(n));
  const earlier = items.filter((n) => !isUnread(n));

  return (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {unreadCount > 0
                ? `You have ${unreadCount} new ${unreadCount === 1 ? 'item' : 'items'}`
                : 'You are all caught up'}
            </Typography>
          </Box>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={markAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {items.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <Iconify icon="eva:bell-off-outline" width={28} height={28} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Nothing needs your attention right now.
            </Typography>
          </Box>
        ) : (
          <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 420 }}>
            {unread.length > 0 && (
              <List
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                    New
                  </ListSubheader>
                }
              >
                {unread.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />
                ))}
              </List>
            )}

            {earlier.length > 0 && (
              <List
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                    Earlier
                  </ListSubheader>
                }
              >
                {earlier.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />
                ))}
              </List>
            )}
          </Scrollbar>
        )}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({
  notification,
  onClick,
}: {
  notification: AppNotification;
  onClick: (n: AppNotification) => void;
}) {
  return (
    <ListItemButton
      onClick={() => onClick(notification)}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: `${notification.color}.lighter`, color: `${notification.color}.darker` }}>
          <Iconify icon={notification.icon} width={22} height={22} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="subtitle2">{notification.title}</Typography>}
        secondary={
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {notification.description}
          </Typography>
        }
      />
      <Iconify icon="eva:arrow-ios-forward-fill" sx={{ color: 'text.disabled', width: 18, height: 18 }} />
    </ListItemButton>
  );
}
